// KHO MẪU (09/10/2026, docs/thiet-ke-kho-mau.md): Super khai báo cách một kịch bản mẫu hiện trong Kho mẫu (nhóm theo MỤC TIÊU, tên cho khách, mô tả,
// mức rủi ro) và những Ô CHO KHÁCH CHỈNH — mỗi ô gắn vào một hay nhiều tham số của các bước trong ảnh chụp mẫu. Boss chỉ điền các ô đó (Cài đặt cơ bản);
// sơ đồ chuyên sâu dành cho người rành. Hàm thuần — máy chủ (server/src/kho-mau.ts) và trang web dùng chung; test ở tests/unit/kho-mau.test.ts.
// Lý do (số liệu thật 09/10): 5/20 bản riêng từ mẫu bị bỏ vào Thùng rác rồi lấy lại mẫu — khách sửa sơ đồ hỏng / không biết sửa.
import { timNenTang, timThaoTac, type BanDo, type ThaoTacThamSo } from "./ban-do";
import { kiemGiaTriThamSo, type SoDo } from "./so-do";

export const NHOM_MAU = [
  { ma: "nuoi_nick", ten: "Nuôi nick (làm nóng tài khoản)" },
  { ma: "seeding_nhom", ten: "Bình luận seeding trong nhóm" },
  { ma: "dang_bai", ten: "Đăng bài" },
  { ma: "tuong_tac_trang", ten: "Tăng tương tác trang / kênh" },
  { ma: "tim_khach", ten: "Tìm khách tiềm năng" },
  { ma: "khac", ten: "Khác" },
] as const;
export type MaNhom = (typeof NHOM_MAU)[number]["ma"];
export const RUI_RO_MAU = { thap: "thấp", vua: "vừa", cao: "cao" } as const;
export type RuiRoMau = keyof typeof RUI_RO_MAU;

export const O_TOI_DA = 12;
export const TEN_TOI_DA = 80;
export const MO_TA_TOI_DA = 200;
export const NHAN_TOI_DA = 60;
export const GIA_TRI_TOI_DA = 2000;

export interface GanO { nutId: string; thamSo: string }
export interface OCoBan {
  khoa: string;
  nhan: string;
  moTa: string;
  kieu: "chuoi" | "so";
  batBuoc: boolean;
  min?: number;
  max?: number;
  luaChon?: string[];
  macDinh: string;
  gan: GanO[];
  /** ĐỢT 3b: số cho từng MỨC ĐỘ (chỉ ô số). Thiếu một mức = mức đó dùng macDinh. Không có trường này = ô không theo mức (dữ liệu cũ). */
  mucDo?: Partial<Record<MucDo, string>>;
}
export interface CaiDatMau { nhom: MaNhom; tenHien: string; moTa: string; ruiRo: RuiRoMau; o: OCoBan[] }
/** Ô GỢI Ý cho Super (goiYO): như OCoBan + tên ngắn các bước dùng ô này (để Super nhận ra). */
export interface OGoiY extends OCoBan { buoc: string[]; phu: boolean } // phu = tham số tuỳ chọn mà bước không đặt rõ (giao diện thu gọn)

type Nut = { id: string; thaoTac: string; thamSo?: Record<string, string>; nenTang?: string };
const dsNut = (sd: SoDo): Nut[] => (Array.isArray(sd?.nut) ? (sd.nut as unknown as Nut[]) : []);
const doDai = (s: string): number => Array.from(s).length;
const KHOA = /^[a-z0-9_]{1,40}$/;

/** Định nghĩa tham số `ten` của bước `nut` theo bản đồ thao tác (mạng của nút, không có = mạng của sơ đồ). */
function dinhNghia(sd: SoDo, bd: BanDo, nut: Nut, ten: string): { def: ThaoTacThamSo; tenNgan: string } | null {
  const tt = timThaoTac(bd, nut.nenTang ?? sd.nenTang, nut.thaoTac);
  const def = tt?.thamSo.find((d) => d.ten === ten);
  return tt && def ? { def, tenNgan: tt.tenNgan } : null;
}

// ---------------------------------------------------------------------------------------------------------------------------------------
/** KU1: mọi tham số của mọi bước; cùng TÊN + cùng KIỂU + cùng GIÁ TRỊ hiện tại → gộp một ô (vd. "Từ khoá" dùng ở 5 bước tìm). Thứ tự = lần xuất hiện đầu. */
export function goiYO(sd: SoDo, bd: BanDo): OGoiY[] {
  const nhom = new Map<string, OGoiY>();
  const daDungKhoa = new Map<string, number>();
  for (const nut of dsNut(sd)) {
    const tt = timThaoTac(bd, nut.nenTang ?? sd.nenTang, nut.thaoTac);
    if (!tt) continue;
    for (const def of tt.thamSo) {
      const datRo = nut.thamSo?.[def.ten] !== undefined;
      const giaTri = nut.thamSo?.[def.ten] ?? def.macDinh ?? "";
      const phu = !!def.anKhiRong && !datRo;
      const k = `${def.ten}\u0000${def.kieu}\u0000${giaTri}\u0000${phu ? 1 : 0}`;
      const co = nhom.get(k);
      if (co) {
        co.gan.push({ nutId: nut.id, thamSo: def.ten });
        if (!co.buoc.includes(tt.tenNgan)) co.buoc.push(tt.tenNgan);
        if (def.min !== undefined) co.min = Math.max(co.min ?? def.min, def.min);
        if (def.max !== undefined) co.max = Math.min(co.max ?? def.max, def.max);
        continue;
      }
      const n = (daDungKhoa.get(def.ten) ?? 0) + 1;
      daDungKhoa.set(def.ten, n);
      nhom.set(k, {
        khoa: n === 1 ? def.ten : `${def.ten}_${n}`, nhan: def.nhan, moTa: "", kieu: def.kieu, batBuoc: !!def.batBuoc,
        ...(def.min !== undefined ? { min: def.min } : {}), ...(def.max !== undefined ? { max: def.max } : {}), ...(def.luaChon ? { luaChon: [...def.luaChon] } : {}),
        macDinh: giaTri, gan: [{ nutId: nut.id, thamSo: def.ten }], buoc: [tt.tenNgan], phu,
      });
    }
  }
  return [...nhom.values()];
}

// ---------------------------------------------------------------------------------------------------------------------------------------
const laSoNguyen = (s: string): boolean => /^-?\d+$/.test(s);
const chuoi = (v: unknown): string | null => (typeof v === "string" ? v : typeof v === "number" && Number.isFinite(v) ? String(v) : null);

/** KU2: kiểm + chuẩn hoá khai báo của Super trên ảnh chụp mẫu `sd`. Lỗi nêu NHÃN ô để Super biết sửa chỗ nào. */
export function kiemCaiDat(vao: unknown, sd: SoDo, bd: BanDo): { ok: true; caiDat: CaiDatMau } | { ok: false; loi: string[] } {
  if (!vao || typeof vao !== "object" || Array.isArray(vao)) return { ok: false, loi: ["Cài đặt phải là một đối tượng"] };
  const v = vao as Record<string, unknown>;
  const loi: string[] = [];
  const nhom = NHOM_MAU.find((n) => n.ma === v.nhom)?.ma;
  if (!nhom) loi.push(`Nhóm không hợp lệ — chọn một trong: ${NHOM_MAU.map((n) => n.ten).join(", ")}`);
  const tenHien = typeof v.tenHien === "string" ? v.tenHien.trim() : "";
  if (!tenHien) loi.push("Tên cho khách không được để trống");
  else if (doDai(tenHien) > TEN_TOI_DA) loi.push(`Tên cho khách tối đa ${TEN_TOI_DA} ký tự (đang có ${doDai(tenHien)})`);
  const moTa = typeof v.moTa === "string" ? v.moTa.trim() : v.moTa === undefined ? "" : null;
  if (moTa === null) loi.push("Mô tả phải là chữ");
  else if (doDai(moTa) > MO_TA_TOI_DA) loi.push(`Mô tả tối đa ${MO_TA_TOI_DA} ký tự (đang có ${doDai(moTa)})`);
  const ruiRo = (Object.keys(RUI_RO_MAU) as RuiRoMau[]).find((r) => r === v.ruiRo);
  if (!ruiRo) loi.push("Mức rủi ro phải là thấp / vừa / cao");
  const dsO = v.o === undefined ? [] : v.o;
  const o: OCoBan[] = [];
  if (!Array.isArray(dsO)) loi.push("Danh sách ô phải là mảng");
  else if (dsO.length > O_TOI_DA) loi.push(`Tối đa ${O_TOI_DA} ô cho khách chỉnh (đang có ${dsO.length})`);
  else {
    const khoaDa = new Set<string>();
    const thamSoDa = new Map<string, string>();
    const nut = new Map(dsNut(sd).map((n) => [n.id, n] as const));
    dsO.forEach((x, i) => {
      const kq = kiemMotO(x, i, sd, bd, nut);
      if (!kq.ok) { loi.push(...kq.loi); return; }
      const ob = kq.o;
      if (khoaDa.has(ob.khoa)) loi.push(`Ô “${ob.nhan}”: mã ô “${ob.khoa}” trùng với ô khác`);
      khoaDa.add(ob.khoa);
      for (const g of ob.gan) {
        const k = `${g.nutId}\u0000${g.thamSo}`;
        const truoc = thamSoDa.get(k);
        if (truoc) loi.push(`Ô “${ob.nhan}” và ô “${truoc}” cùng gắn vào một tham số — một tham số chỉ thuộc một ô, không được hai ô`);
        else thamSoDa.set(k, ob.nhan);
      }
      o.push(ob);
    });
  }
  if (loi.length) return { ok: false, loi };
  return { ok: true, caiDat: { nhom: nhom!, tenHien, moTa: moTa ?? "", ruiRo: ruiRo!, o } };
}

function kiemMotO(x: unknown, i: number, sd: SoDo, bd: BanDo, nut: Map<string, Nut>): { ok: true; o: OCoBan } | { ok: false; loi: string[] } {
  if (!x || typeof x !== "object" || Array.isArray(x)) return { ok: false, loi: [`Ô thứ ${i + 1} không hợp lệ`] };
  const v = x as Record<string, unknown>;
  const nhan = typeof v.nhan === "string" ? v.nhan.trim() : "";
  const ten = nhan || `thứ ${i + 1}`;
  const loi: string[] = [];
  if (!nhan) loi.push(`Ô thứ ${i + 1}: Nhãn không được để trống`);
  else if (doDai(nhan) > NHAN_TOI_DA) loi.push(`Ô “${ten}”: Nhãn tối đa ${NHAN_TOI_DA} ký tự`);
  const khoa = typeof v.khoa === "string" ? v.khoa : "";
  if (!KHOA.test(khoa)) loi.push(`Ô “${ten}”: mã ô chỉ gồm a-z, 0-9, _ (1–40 ký tự)`);
  const moTa = typeof v.moTa === "string" ? v.moTa.trim() : "";
  if (doDai(moTa) > MO_TA_TOI_DA) loi.push(`Ô “${ten}”: mô tả tối đa ${MO_TA_TOI_DA} ký tự`);
  const kieu = v.kieu === "so" || v.kieu === "chuoi" ? v.kieu : null;
  if (!kieu) loi.push(`Ô “${ten}”: kiểu phải là chữ hoặc số`);
  const batBuoc = v.batBuoc === true;
  // Gắn: mỗi phần tử trỏ vào bước CÓ trong ảnh chụp và tham số CÓ ở thao tác của bước đó, cùng kiểu với ô.
  const gan: GanO[] = [];
  let minBd = -Infinity, maxBd = Infinity;
  let luaChonBd: string[] | undefined;
  if (!Array.isArray(v.gan) || !v.gan.length) loi.push(`Ô “${ten}”: phải gắn vào ít nhất một bước`);
  else for (const g of v.gan as unknown[]) {
    const gg = g as Partial<GanO> | null;
    const n = typeof gg?.nutId === "string" ? nut.get(gg.nutId) : undefined;
    const dn = n && typeof gg?.thamSo === "string" ? dinhNghia(sd, bd, n, gg.thamSo) : null;
    if (!n || !dn) { loi.push(`Ô “${ten}”: bước / tham số được gắn không có trong mẫu (${String(gg?.nutId ?? "?")}.${String(gg?.thamSo ?? "?")})`); continue; }
    if (kieu && dn.def.kieu !== kieu) { loi.push(`Ô “${ten}”: kiểu ô (${kieu === "so" ? "số" : "chữ"}) lệch với tham số “${dn.def.nhan}” của bước “${dn.tenNgan}”`); continue; }
    if (dn.def.min !== undefined) minBd = Math.max(minBd, dn.def.min);
    if (dn.def.max !== undefined) maxBd = Math.min(maxBd, dn.def.max);
    if (dn.def.luaChon) luaChonBd = luaChonBd ? luaChonBd.filter((c) => dn.def.luaChon!.includes(c)) : [...dn.def.luaChon];
    gan.push({ nutId: n.id, thamSo: dn.def.ten });
  }
  const macDinhVao = chuoi(v.macDinh) ?? "";
  const macDinh = macDinhVao.trim();
  const out: OCoBan = { khoa, nhan, moTa, kieu: kieu ?? "chuoi", batBuoc, macDinh, gan };
  if (kieu === "so") {
    const min = v.min === undefined ? (Number.isFinite(minBd) ? minBd : undefined) : typeof v.min === "number" && Number.isInteger(v.min) ? v.min : NaN;
    const max = v.max === undefined ? (Number.isFinite(maxBd) ? maxBd : undefined) : typeof v.max === "number" && Number.isInteger(v.max) ? v.max : NaN;
    if (Number.isNaN(min) || Number.isNaN(max)) loi.push(`Ô “${ten}”: giới hạn phải là số nguyên`);
    else {
      if (min !== undefined && max !== undefined && min > max) loi.push(`Ô “${ten}”: số nhỏ nhất (${min}) lớn hơn số lớn nhất (${max})`);
      const rong = (min !== undefined && min < minBd) || (max !== undefined && max > maxBd);
      if (rong) loi.push(`Ô “${ten}”: giới hạn phải nằm trong ${Number.isFinite(minBd) ? minBd : "…"}–${Number.isFinite(maxBd) ? maxBd : "…"} (bot chỉ nhận chừng đó)`);
      if (!laSoNguyen(macDinh) || (min !== undefined && Number(macDinh) < min) || (max !== undefined && Number(macDinh) > max)) loi.push(`Ô “${ten}”: giá trị mặc định “${macDinh}” phải là số nguyên trong ${min ?? "…"}–${max ?? "…"}`);
      if (min !== undefined) out.min = min;
      if (max !== undefined) out.max = max;
    }
  } else if (kieu === "chuoi") {
    if (batBuoc && !macDinh) loi.push(`Ô “${ten}”: bắt buộc nên phải có giá trị mặc định`);
    if (doDai(macDinh) > GIA_TRI_TOI_DA) loi.push(`Ô “${ten}”: giá trị mặc định tối đa ${GIA_TRI_TOI_DA} ký tự`);
    const lc = Array.isArray(v.luaChon) ? (v.luaChon as unknown[]).filter((s): s is string => typeof s === "string") : luaChonBd;
    if (lc) {
      if (luaChonBd && lc.some((c) => !luaChonBd!.includes(c))) loi.push(`Ô “${ten}”: lựa chọn chỉ được trong: ${luaChonBd.join(", ")}`);
      if (macDinh && !lc.includes(macDinh)) loi.push(`Ô “${ten}”: giá trị mặc định phải là một lựa chọn`);
      out.luaChon = lc;
    }
  }
  const md = kiemMucDo(v.mucDo, out, ten); // ĐỢT 3b — mức độ đi theo ô (Cập nhật mẫu kiểm lại cùng hàm này)
  loi.push(...md.loi);
  if (md.mucDo) out.mucDo = md.mucDo;
  return loi.length ? { ok: false, loi } : { ok: true, o: out };
}

// ---------------------------------------------------------------------------------------------------------------------------------------
/** KU2b: giá trị Boss điền. Thiếu → mặc định; số nhận cả number; cắt khoảng trắng. Lỗi nêu nhãn ô. */
export function kiemGiaTri(o: OCoBan[], vao: unknown): { ok: true; gt: Record<string, string> } | { ok: false; loi: string[] } {
  if (!vao || typeof vao !== "object" || Array.isArray(vao)) return { ok: false, loi: ["Giá trị các ô phải là một đối tượng"] };
  const v = vao as Record<string, unknown>;
  const loi: string[] = [];
  const theoKhoa = new Map(o.map((x) => [x.khoa, x] as const));
  for (const k of Object.keys(v)) if (!theoKhoa.has(k)) loi.push(`Ô “${k}” không có trong mẫu`);
  const gt: Record<string, string> = {};
  for (const x of o) {
    const tho = v[x.khoa];
    const s0 = tho === undefined || tho === null ? x.macDinh : chuoi(tho);
    if (s0 === null) { loi.push(`“${x.nhan}”: giá trị không hợp lệ`); continue; }
    const s = s0.trim();
    if (x.kieu === "so") {
      if (!laSoNguyen(s)) { loi.push(`“${x.nhan}” phải là số nguyên`); continue; }
      const n = Number(s);
      if ((x.min !== undefined && n < x.min) || (x.max !== undefined && n > x.max)) { loi.push(`“${x.nhan}” phải trong khoảng ${x.min ?? "…"}–${x.max ?? "…"}`); continue; }
      gt[x.khoa] = String(n);
    } else {
      if (x.batBuoc && !s) { loi.push(`“${x.nhan}” không được để trống`); continue; }
      if (doDai(s) > GIA_TRI_TOI_DA) { loi.push(`“${x.nhan}” tối đa ${GIA_TRI_TOI_DA} ký tự`); continue; }
      if (x.luaChon && s && !x.luaChon.includes(s)) { loi.push(`“${x.nhan}” phải là một trong: ${x.luaChon.join(", ")}`); continue; }
      gt[x.khoa] = s;
    }
  }
  return loi.length ? { ok: false, loi } : { ok: true, gt };
}

/** KU2d: câu lỗi NGẮN hiện ngay dưới MỘT ô trên trang (kiểm ở trình duyệt trước khi gửi — máy chủ vẫn kiểm lại đủ). null = hợp lệ. */
export function loiMotO(o: Pick<OCoBan, "kieu" | "batBuoc"> & Partial<OCoBan>, vao: string): string | null {
  const v = vao.trim();
  if (o.kieu === "so") {
    if (!laSoNguyen(v)) return "Phải là số nguyên";
    const n = Number(v);
    if ((o.min !== undefined && n < o.min) || (o.max !== undefined && n > o.max)) return `Phải trong khoảng ${o.min ?? "…"}–${o.max ?? "…"}`;
    return null;
  }
  if (!v) return o.batBuoc ? "Không được để trống" : null;
  if (v.includes('"')) return "Không được chứa dấu nháy kép (\")";
  if (/[\r\n]/.test(v)) return "Không được xuống dòng";
  if (doDai(v) > GIA_TRI_TOI_DA) return `Tối đa ${GIA_TRI_TOI_DA} ký tự`;
  if (o.luaChon && !o.luaChon.includes(v)) return `Chọn một trong: ${o.luaChon.join(", ")}`;
  return null;
}

/** KU2c: giá trị đã qua kiemGiaTri còn phải qua ĐÚNG luật của bộ kiểm sơ đồ cho từng tham số được gắn (nháy kép, xuống dòng, địa chỉ đúng mạng, danh sách
 *  từ khoá… — so-do.ts kiemGiaTriThamSo, một bộ luật duy nhất). Lỗi nêu NHÃN ô; một ô gắn nhiều bước báo mỗi câu một lần; bước không còn → bỏ qua. */
export function kiemGiaTriTheoSoDo(sd: SoDo, bd: BanDo, o: OCoBan[], gt: Record<string, string>): string[] {
  const theoId = new Map(dsNut(sd).map((n) => [n.id, n] as const));
  const loi: string[] = [];
  for (const x of o) {
    const v = gt[x.khoa] ?? x.macDinh;
    if (!v.trim()) continue; // để trống: kiemGiaTri đã xét bắt buộc
    for (const g of x.gan) {
      const n = theoId.get(g.nutId);
      const dn = n ? dinhNghia(sd, bd, n, g.thamSo) : null;
      if (!n || !dn) continue;
      const mang = n.nenTang ?? sd.nenTang;
      for (const l of kiemGiaTriThamSo(`“${x.nhan}”`, dn.def, v, mang, timNenTang(bd, mang)).loi) if (!loi.includes(l)) loi.push(l);
    }
  }
  return loi;
}

/** KU3: ghi giá trị vào MỌI bước được gắn (bản sao — không sửa đầu vào). Ô không còn bước nào → boQua (nhãn). doi = có tham số thật sự đổi. */
export function apGiaTri(sd: SoDo, o: OCoBan[], gt: Record<string, string>): { soDo: SoDo; boQua: string[]; doi: boolean } {
  const nut = dsNut(sd).map((n) => ({ ...n, thamSo: { ...(n.thamSo ?? {}) } }));
  const theoId = new Map(nut.map((n) => [n.id, n] as const));
  const boQua: string[] = [];
  let doi = false;
  for (const x of o) {
    const v = gt[x.khoa] ?? x.macDinh;
    let ap = false;
    for (const g of x.gan) {
      const n = theoId.get(g.nutId);
      if (!n) continue;
      ap = true;
      if (n.thamSo[g.thamSo] !== v) { n.thamSo[g.thamSo] = v; doi = true; }
    }
    if (!ap) boQua.push(x.nhan);
  }
  return { soDo: { ...sd, nut } as unknown as SoDo, boQua, doi };
}

/** KU4: giá trị hiện tại của từng ô — bước gắn đầu tiên còn tồn tại và có tham số đó; không có → mặc định. */
export function docGiaTri(sd: SoDo, o: OCoBan[]): Record<string, string> {
  const theoId = new Map(dsNut(sd).map((n) => [n.id, n] as const));
  const out: Record<string, string> = {};
  for (const x of o) {
    let v: string | undefined;
    for (const g of x.gan) { const n = theoId.get(g.nutId); if (n?.thamSo?.[g.thamSo] !== undefined) { v = n.thamSo[g.thamSo]; break; } }
    out[x.khoa] = v ?? x.macDinh;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------------------------------
const cat = (s: string, n: number): string => Array.from(s).slice(0, Math.max(0, n)).join("").trimEnd();
const chuanTen = (s: string): string => s.trim().toLocaleLowerCase("vi");

/** KU5: "<tên mẫu> · <sản phẩm>" (không sản phẩm → tên mẫu); trùng tên đã có (không phân biệt hoa thường) → " (2)", " (3)"…; tối đa 80 ký tự kể cả hậu tố. */
export function tenGoiY(tenMau: string, tenSp: string | null, daCo: string[]): string {
  const goc = (tenSp ? `${tenMau.trim()} · ${tenSp.trim()}` : tenMau.trim()) || "Kịch bản từ mẫu";
  const co = new Set(daCo.map(chuanTen));
  const dau = cat(goc, TEN_TOI_DA);
  if (!co.has(chuanTen(dau))) return dau;
  for (let n = 2; ; n++) {
    const hau = ` (${n})`;
    const t = cat(goc, TEN_TOI_DA - doDai(hau)) + hau;
    if (!co.has(chuanTen(t))) return t;
  }
}

// ---------------------------------------------------------------------------------------------------------------------------------------
// ĐỢT 2 (docs/thiet-ke-kho-mau.md mục 7): theo mẫu / tách khỏi mẫu / tự nhận cập nhật / quay về theo mẫu. Test: tests/unit/kho-mau-dot2.test.ts.

/** DU1: giá trị khách đã điền (theo KHOÁ ô) → giá trị cho bộ ô MỚI. Ô mới → mặc định; ô bị bỏ → bỏ; giá trị cũ không còn hợp với ô mới (kiểu, giới hạn,
 *  lựa chọn, bắt buộc; có `sd` + `bd` thì cả luật bộ kiểm sơ đồ) → mặc định và nêu NHÃN ô trong `veMacDinh`. Giá trị cũ hỏng (không phải đối tượng) = chưa điền gì. */
export function gopGiaTri(o: OCoBan[], giaTriCu: unknown, sd?: SoDo, bd?: BanDo): { gt: Record<string, string>; veMacDinh: string[] } {
  const cu = giaTriCu && typeof giaTriCu === "object" && !Array.isArray(giaTriCu) ? (giaTriCu as Record<string, unknown>) : {};
  const gt: Record<string, string> = {};
  const veMacDinh: string[] = [];
  for (const x of o) {
    const coCu = Object.prototype.hasOwnProperty.call(cu, x.khoa) && cu[x.khoa] !== undefined && cu[x.khoa] !== null;
    if (!coCu) { gt[x.khoa] = x.macDinh; continue; }
    const kq = kiemGiaTri([x], { [x.khoa]: cu[x.khoa] });
    if (kq.ok && !(sd && bd && kiemGiaTriTheoSoDo(sd, bd, [x], kq.gt).length)) gt[x.khoa] = kq.gt[x.khoa]!;
    else { gt[x.khoa] = x.macDinh; veMacDinh.push(x.nhan); }
  }
  return { gt, veMacDinh };
}

/** DU2: NỘI DUNG CHI TIẾT của sơ đồ (bước, cạnh, mạng, giới hạn, vòng lặp…) — bỏ mã, tên, yêu cầu gốc, bài học AI và SẢN PHẨM (đổi sản phẩm không làm
 *  tách khỏi mẫu: Cài đặt cơ bản cũng đổi được sản phẩm). Khoá sắp xếp → không lệ thuộc thứ tự. Lưu sơ đồ mà chuỗi này đổi = đã chỉnh chi tiết → tách. */
export function noiDungSoDo(sd: SoDo | null | undefined): string {
  if (!sd || typeof sd !== "object") return "";
  const { id: _id, ten: _ten, yeuCau: _yc, kinhNghiem: _kn, hoSo: _hs, ...con } = sd as SoDo & Record<string, unknown>;
  const sapXep = (v: unknown): unknown => (Array.isArray(v) ? v.map(sapXep)
    : v && typeof v === "object" ? Object.fromEntries(Object.keys(v as object).sort().filter((k) => (v as Record<string, unknown>)[k] !== undefined).map((k) => [k, sapXep((v as Record<string, unknown>)[k])]))
    : v);
  return JSON.stringify(sapXep(con));
}

// ĐỢT 3b — MỨC ĐỘ Nhẹ / Vừa / Mạnh (docs/thiet-ke-kho-mau.md mục "Đợt 3b"). Super đặt số cho từng mức trên các Ô SỐ; Boss chỉ chọn mức (vẫn sửa số được trong
// giới hạn). Mức gói trong chính ô (OCoBan.mucDo → o_json) nên "Cập nhật mẫu" kiểm mức cùng lúc kiểm ô; máy chủ KHÔNG lưu mức Boss chọn — giá trị ô là đủ,
// mức hiển thị lại được bằng mucKhop(o, giá trị). Test: tests/unit/kho-mau-muc-do.test.ts.
export const MUC_DO = [
  { ma: "nhe", ten: "Nhẹ", moTa: "an toàn cho nick mới, ít việc mỗi ngày" },
  { ma: "vua", ten: "Vừa", moTa: "cân bằng — cho nick đã dùng vài tuần" },
  { ma: "manh", ten: "Mạnh", moTa: "nhiều việc mỗi ngày — chỉ nên dùng cho nick lâu năm" },
] as const;
export type MucDo = (typeof MUC_DO)[number]["ma"];
export const MUC_DO_MAC_DINH: MucDo = "vua";
const TEN_MUC: Record<MucDo, string> = { nhe: "Nhẹ", vua: "Vừa", manh: "Mạnh" };
const laMuc = (k: string): k is MucDo => k === "nhe" || k === "vua" || k === "manh";

/** Kiểm + chuẩn hoá mucDo của MỘT ô (ô đã chuẩn hoá: kieu/min/max/macDinh). Trống / null → bỏ mức đó; cả 3 trống → undefined. Lỗi nêu tên ô. */
function kiemMucDo(vao: unknown, o: OCoBan, ten: string): { mucDo?: Partial<Record<MucDo, string>>; loi: string[] } {
  if (vao === undefined || vao === null) return { loi: [] };
  if (typeof vao !== "object" || Array.isArray(vao)) return { loi: [`Ô “${ten}”: mức độ phải gồm Nhẹ / Vừa / Mạnh`] };
  const loi: string[] = [];
  const out: Partial<Record<MucDo, string>> = {};
  for (const [k, tho] of Object.entries(vao as Record<string, unknown>)) {
    if (!laMuc(k)) { loi.push(`Ô “${ten}”: mức “${cat(k, 20)}” không có — chỉ có Nhẹ / Vừa / Mạnh`); continue; }
    if (tho === undefined || tho === null) continue;
    const s0 = chuoi(tho);
    const s = s0?.trim() ?? null;
    if (s === "") continue;
    if (o.kieu !== "so") { loi.push(`Ô “${ten}”: mức độ Nhẹ / Vừa / Mạnh chỉ dùng cho ô số`); return { loi }; }
    const n = s !== null && laSoNguyen(s) ? Number(s) : NaN;
    const ngoai = Number.isSafeInteger(n) && ((o.min !== undefined && n < o.min) || (o.max !== undefined && n > o.max));
    if (!Number.isSafeInteger(n) || ngoai) {
      loi.push(`Ô “${ten}”: mức ${TEN_MUC[k]} “${cat(s ?? String(tho), 20)}” phải là số nguyên trong ${o.min ?? "…"}–${o.max ?? "…"}`);
      continue;
    }
    out[k] = String(n);
  }
  if (loi.length) return { loi };
  if (!Object.keys(out).length) return { loi: [] };
  // Thứ tự Nhẹ ≤ Vừa ≤ Mạnh — mức để trống so bằng mặc định của ô (đúng con số khách sẽ nhận).
  const md = laSoNguyen(o.macDinh) ? o.macDinh : undefined;
  const so = MUC_DO.map((m) => ({ ten: m.ten, v: out[m.ma] ?? md })).filter((x): x is { ten: "Nhẹ" | "Vừa" | "Mạnh"; v: string } => x.v !== undefined);
  for (let i = 0; i < so.length; i++) for (let j = i + 1; j < so.length; j++) {
    if (Number(so[i]!.v) > Number(so[j]!.v)) {
      return { loi: [`Ô “${ten}”: phải Nhẹ ≤ Vừa ≤ Mạnh (đang ${so[i]!.ten} ${so[i]!.v} > ${so[j]!.ten} ${so[j]!.v}; mức để trống dùng mặc định ${md ?? "—"})`] };
    }
  }
  return { mucDo: out, loi: [] };
}

type OMuc = Pick<OCoBan, "khoa" | "kieu" | "macDinh" | "mucDo">;
const coMuc = (o: OMuc): boolean => o.kieu === "so" && !!o.mucDo && Object.values(o.mucDo).some((v) => typeof v === "string" && v.trim() !== "");

/** Mẫu có "mức độ" khi có ít nhất một ô số có ít nhất một mức. */
export function coMucDo(o: OMuc[]): boolean { return o.some(coMuc); }

/** Giá trị các ô THEO MỨC — chỉ ô có mức (ô khác giữ số Boss đang điền); mức trống → mặc định của ô. */
export function giaTriMuc(o: OMuc[], muc: MucDo): Record<string, string> {
  const out: Record<string, string> = {};
  for (const x of o) if (coMuc(x)) out[x.khoa] = x.mucDo?.[muc] ?? x.macDinh;
  return out;
}

/** Giá trị đang điền khớp mức nào (mọi ô có mức đều bằng số của mức đó). Nhiều mức cùng số → ưu tiên `uuTien`, rồi theo thứ tự Nhẹ → Mạnh.
 *  Không ô nào có mức / sửa tay khác mọi mức / ô không phải số nguyên → null ("tuỳ chỉnh"). */
export function mucKhop(o: OMuc[], gt: Record<string, string>, uuTien?: MucDo): MucDo | null {
  const ds = o.filter(coMuc);
  if (!ds.length) return null;
  const thu = uuTien ? [uuTien, ...MUC_DO.map((m) => m.ma).filter((m) => m !== uuTien)] : MUC_DO.map((m) => m.ma);
  const khop = (muc: MucDo): boolean => ds.every((x) => {
    const v = (gt[x.khoa] ?? "").trim();
    return laSoNguyen(v) && Number(v) === Number(x.mucDo?.[muc] ?? x.macDinh);
  });
  return thu.find(khop) ?? null;
}

// ---------------------------------------------------------------------------------------------------------------------------------------
/** (10/10/2026) Câu báo Super sau "Cập nhật mẫu": phiên bản + số kịch bản của khách đã TỰ cập nhật / chưa cập nhật được (đợt 2 trả tuCapNhat).
 *  Máy chủ cũ không trả tuCapNhat / số hỏng → như 0. Dùng ở kich-ban-mau.ts (thẻ mẫu nguồn) và kich-ban-5-sao.ts (hộp ★ Cấp phát). */
export function chuCapNhatMau(r: { phienBan: number; doiNoiDung: boolean; tuCapNhat?: { daCapNhat?: number; loi?: number } | null }): string {
  if (!r.doiNoiDung) return "Sơ đồ chưa đổi so với mẫu — giữ phiên bản cũ";
  const so = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
  const da = so(r.tuCapNhat?.daCapNhat), loi = so(r.tuCapNhat?.loi);
  return [`Đã cập nhật mẫu — phiên bản ${r.phienBan}`,
    ...(da ? [`đã tự cập nhật ${da} kịch bản của khách`] : []),
    ...(loi ? [`${loi} kịch bản chưa cập nhật được (khách đã được báo lý do)`] : [])].join(" · ");
}
